import React from "react";
import ReactDOM from "react-dom";

// I don't love this whole runtime-object-prototype-creation scheme, but it's
// actually the way all of google's plugin examples want you to do it, and helps
// with our dynamically-loaded gmaps api stuff, so here it is.
let CustomPopup;

export class GoogleDivOverlay extends React.PureComponent {
  static defaultProps = {
    offset: [0, 0],
    autoPan: false,
  };

  constructor(props) {
    super(props);
    this._container = document.createElement("div");
  }

  componentDidMount() {
    const { mapInstance, position, offset, interactive, pane, autoPan } = this.props;
    definePopupClass();
    this._divOverlay = new CustomPopup({
      content: this._container,
      position: position,
      offset,
      interactive,
      pane,
      autoPan,
    });
    // if mapInstance isn't a rwgps.Map, assume it's a direct reference to the gmap
    const map = mapInstance.gmap || mapInstance;
    this._divOverlay.setMap(map);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.position !== this.props.position) {
      this._divOverlay.setPosition(this.props.position);
    }
    this._divOverlay.draw();
  }

  componentWillUnmount() {
    this._divOverlay.setMap(null);
    delete this._divOverlay;
  }

  render() {
    const { children, minWidth } = this.props;
    return ReactDOM.createPortal(<div style={{ minWidth }}>{children}</div>, this._container);
  }
}

// externally we allow the use of leaflet pane names, this map translates to corresponding GMaps panes
const paneMap = {
  tilePane: "mapPane",
  shadowPane: "overlayLayer",
  overlayPane: "overlayLayer",
  markerPane: "markerLayer",
  tooltipPane: "floatPane",
};

// adapted from https://developers.google.com/maps/documentation/javascript/examples/overlay-popup
const definePopupClass = () => {
  CustomPopup = function ({ position, content, offset, interactive, pane, ...opts }) {
    this._pane = pane;
    this.opts = opts;
    this.position = new window.google.maps.LatLng(position.lat, position.lng);
    this.offset = offset;
    this.interactive = !!interactive;
    this.anchor = document.createElement("div");
    this.anchor.classList.add("popup-tip-anchor");
    this.anchor.style.position = "absolute";
    // disabling for now - this is causing weirdness when zooming
    // this.anchor.style.transition = 'left 150ms ease-in-out, top 150ms ease-in-out'
    this.anchor.style.boxSizing = "border-box";
    this.anchor.appendChild(content);

    // Optionally stop clicks, etc., from bubbling up to the map.
    this.stopEventPropagation();
  };

  // NOTE: google.maps.OverlayView is only defined once the Maps API has
  // loaded. That's why we do this in a function that runs in componentDidMount.
  //
  // Side note: I **love** how every mapping framework likes to invent their own
  // version of object inheritance in JS. Love it.
  CustomPopup.prototype = Object.create(window.google.maps.OverlayView.prototype);

  CustomPopup.prototype.onAdd = function () {
    this.getPanes()[paneMap[this._pane]].appendChild(this.anchor);
    if (this.opts.autoPan) {
      this.panIntoViewport();
    }
  };

  CustomPopup.prototype.panIntoViewport = function () {
    const PADDING = 80; // just enough to stay out of the way of the map settings button and zoom buttons

    const projection = this.getProjection();
    const map = this.getMap();
    if (!map || !projection) {
      console.warn("panIntoViewport called without a map attached");
      return;
    }

    const viewPosition = projection.fromLatLngToContainerPixel(this.position);
    const viewportWidth = map.getDiv().offsetWidth;
    const viewportHeight = map.getDiv().offsetHeight;
    const contentWidth = this.anchor.clientWidth;
    const contentHeight = this.anchor.clientHeight;
    const outsideLeftBound = viewPosition.x < contentWidth / 2;
    const outsideRightBound = viewPosition.x > viewportWidth - contentWidth / 2;
    const outsideTopBound = viewPosition.y < contentHeight;
    const outsideBottomBound = viewPosition.y > viewportHeight;

    if (outsideLeftBound || outsideTopBound || outsideRightBound || outsideBottomBound) {
      let x = 0;
      let y = 0;
      if (outsideTopBound) {
        y = viewPosition.y - contentHeight - PADDING;
      } else if (outsideBottomBound) {
        y = viewPosition.y - viewportHeight + PADDING;
      }

      if (outsideLeftBound) {
        x = viewPosition.x - contentWidth / 2 - PADDING;
      } else if (outsideRightBound) {
        x = viewPosition.x - viewportWidth + contentWidth / 2 + PADDING;
      }

      map.panBy(x, y);
    }
  };

  CustomPopup.prototype.onRemove = function () {
    if (this.anchor.parentElement) {
      this.anchor.parentElement.removeChild(this.anchor);
    }
  };

  CustomPopup.prototype.draw = function () {
    const projection = this.getProjection();
    if (!projection) {
      return;
    } // we don't have a map yet, bail
    var divPosition = projection.fromLatLngToDivPixel(this.position);
    const contentWidth = this.anchor.clientWidth;
    const contentHeight = this.anchor.clientHeight;
    const offset = this.offset;

    const inRenderBounds = this.opts.autoPan || (Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000);

    // Hide the overlay when it is far out of view.
    var display = inRenderBounds ? "block" : "none";

    if (display === "block") {
      this.anchor.style.left = divPosition.x - contentWidth / 2 + offset[0] + "px";
      this.anchor.style.top = divPosition.y - contentHeight + offset[1] + "px";
    }
    if (this.anchor.style.display !== display) {
      this.anchor.style.display = display;
    }
  };

  CustomPopup.prototype.setPosition = function (position) {
    this.position = new window.google.maps.LatLng(position.lat, position.lng);
    this.draw();
  };

  /** Stops clicks/drags from bubbling up to the map. */
  CustomPopup.prototype.stopEventPropagation = function () {
    var anchor = this.anchor;
    anchor.style.cursor = "auto";

    // if we're in "interactive mode", don't allow dragging the map by grabbing the
    // div. If we're not, just prevent click events on the map
    const events = this.interactive
      ? ["click", "dblclick", "contextmenu", "wheel", "mousedown", "touchstart", "pointerdown"]
      : ["click", "dblclick", "contextmenu"];

    events.forEach(function (event) {
      anchor.addEventListener(event, function (e) {
        e.stopPropagation();
      });
    });
  };
};
